import { useEffect, useState, useMemo, useCallback } from 'react';

import { useConfigContext } from '../context/ConfigContext';
import { FlowContext, IFlowContextProps } from '../context/FlowContext';
import { useUserContext } from '../context/UserContext';
import { useVehicleContext } from '../context/VehicleContext';
import { useValidationContext } from '../context/ValidationContext';
import { SET_CHECK_RISC } from '../store/ActionKeys';
import { pages } from '../PageConfig';
import { useStateValue } from '../hooks/UseStateValue';
import { BLOCK_TYPES, IRouterPage, ProductPage, TProvider } from '../interfaces/interfaces';
import { riscCheckCall } from '../helpers/shared';

const FlowProvider = ({ children }: TProvider) => {
    const [{ selection, model, error, activePage, checkRisc }, dispatch] = useStateValue();
    const config = useConfigContext();
    const productPages = pages[config.product];
    const vc = useValidationContext();
    const [flow, setFlow] = useState(productPages);
    const user = useUserContext();
    const { isBornholm, ageValid, canProceed, isNeedOtherCoverage } = useUserContext();
    const { olderCar, carNotInList, validLeasing, tariffGroup, carCanProceed, manufacturerExcluded, noContent3738 } =
        useVehicleContext();
    const [flowBlocked, setFlowBlocked] = useState<boolean>(false);
    const [blockType, setBlockType] = useState<BLOCK_TYPES | undefined>();
    const [kvhx, setKvhx] = useState('');

    /**
     * Set block type
     * TODO: Block check properties should depend on product type
     */
    useEffect(() => {
        if (!validLeasing) {
            // age and leasing combination is not acceptable
            setBlockType(BLOCK_TYPES.LEASING_AGE_INCOMPATIBLE);
        } else if (error.manufacturerDataDeficient) {
            // we do not have data about this car
            setBlockType(BLOCK_TYPES.CAR_DATA_DEFICIENT);
        } else if (olderCar) {
            // old car / veteran bil not valid for coverage
            setBlockType(BLOCK_TYPES.CAR_TOO_OLD);
        } else if (carNotInList) {
            // manufactor or any of the car details is not in the list
            setBlockType(BLOCK_TYPES.CAR_NOT_IN_LIST);
        } else if (!ageValid) {
            // too young (WE DONT HAVE A MAX AGE)
            setBlockType(BLOCK_TYPES.CUSTOMER_BELOW_18);
        } else if (selection.numberOfClaims && selection.numberOfClaims > 3) {
            // current flow does not support more than 3 claims
            setBlockType(BLOCK_TYPES.TOO_MANY_CLAIMS);
        } else if (manufacturerExcluded) {
            // car manufactor is on the excludedManufacturers list (very expensive cars)
            setBlockType(BLOCK_TYPES.EXCLUDED_MANUFACTURER);
        } else if (noContent3738 && activePage.key === 'summary' && flowBlocked) {
            // customer is not helkunde and tariffgroup is higher than 36
            setBlockType(BLOCK_TYPES.CAR_NO_CONTENT_37_38);
        } else if (isBornholm) {
            // customer selects an address on the island of Bornholm
            setBlockType(BLOCK_TYPES.UNSUPPORTED_ADDRESS_BORNHOLM);
        } else if (user.addressInBBR === false) {
            // BBR does not have the address
            setBlockType(BLOCK_TYPES.UNSUPPORTED_ADDRESS_BBR);
        } else if (!carCanProceed) {
            // car is already insured at Topdanmark
            setBlockType(BLOCK_TYPES.CAR_ALREADY_COVERED);
        } else if (!canProceed) {
            // customer has customerMarking
            setBlockType(BLOCK_TYPES.CUSTOMER_MARKING);
        } else if (error.hasError) {
            // an error happened, DOH!!
            setBlockType(BLOCK_TYPES.GENERAL_ERROR);
        } else if (model['CAR']?.dfimStatus === false) {
            if (activePage.key === 'experience') {
                //early login
                setBlockType(BLOCK_TYPES.D_STATUS_EARLY);
            } else if (activePage.key === 'finalize') {
                //late login
                setFlowBlocked(true);
                setBlockType(BLOCK_TYPES.D_STATUS_LATE);
            }
        } else if (isNeedOtherCoverage) {
            setBlockType(BLOCK_TYPES.CAR_OTHER_NEEDS);
        } else {
            setBlockType(undefined);
        }
    }, [
        validLeasing,
        olderCar,
        carNotInList,
        ageValid,
        selection.numberOfClaims,
        tariffGroup,
        isBornholm,
        carCanProceed,
        canProceed,
        manufacturerExcluded,
        error.hasError,
        model,
        user.addressInBBR,
        activePage.key,
        error.manufacturerDataDeficient,
        isNeedOtherCoverage,
        noContent3738,
        flowBlocked
    ]);

    useEffect(() => {
        setFlowBlocked(!!blockType);
    }, [blockType]);

    useEffect(() => {
        if (!user.loggedIn && checkRisc && selection?.address?.kvhx && selection?.address?.kvhx !== kvhx) {
            setKvhx(selection?.address?.kvhx || '');
            dispatch({
                type: SET_CHECK_RISC,
                payload: false
            });

            riscCheckCall(config.endpoint.riscCheck, selection?.address?.kvhx ?? '');
        }
    }, [checkRisc, selection?.address?.kvhx, config.endpoint.riscCheck, user.loggedIn, kvhx, dispatch]);

    // Adjust flow for FLOW TYPE
    useEffect(() => {
        productPages.sort((a: ProductPage, b: ProductPage) => a.order - b.order);
        const customerPages = productPages.filter((p: ProductPage) => p.flow !== 'NONCUSTOMER');
        const nonCustomerPages = productPages.filter((p: ProductPage) => p.flow !== 'CUSTOMER');
        const specificPages = user.loggedIn ? customerPages : nonCustomerPages;
        setFlow(specificPages);
    }, [productPages, user.loggedIn, config.product, model]);

    const getNextCustomerPage = useCallback(
        (page: IRouterPage) => {
            const currentIndex = flow.indexOf(page);
            const nextPage = flow.find((p, index: number) => p.flow !== 'NONCUSTOMER' && index > currentIndex);
            return nextPage;
        },
        [flow]
    );

    const canContinue = useCallback(
        (page: IRouterPage): boolean => {
            const iFields = vc.invalidFields(page);
            return !iFields.length && !flowBlocked;
        },
        [vc, flowBlocked]
    );

    const items: IFlowContextProps = useMemo(
        () => ({
            flow,
            flowBlocked,
            blockType,
            canContinue,
            getNextCustomerPage,
            setFlowBlocked
        }),
        [flow, flowBlocked, blockType, canContinue, getNextCustomerPage, setFlowBlocked]
    );

    return <FlowContext.Provider value={items}>{children}</FlowContext.Provider>;
};

export default FlowProvider;
